import { useEffect, useRef, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Bell, BellRing, Footprints, Bus, Clock, Navigation, Zap, ZapOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cacheGet, cacheSet } from "@/lib/offline";
import { enablePush, disablePush } from "@/lib/push";
import DirectionsModal from "@/components/DirectionsModal";

const CENTER = [30.6146, -96.3417];
const todayName = () => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date().getDay()];
const toMin = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
const label = (mins) => {
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const ap = h < 12 ? "AM" : "PM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function ReminderBanner({ classesVersion, userLoc, rain, accessible, semester, pref }) {
  const [enabled, setEnabled] = useState(() => cacheGet("remindersOn") || false);
  const [todayPlan, setTodayPlan] = useState(null);
  const [now, setNow] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });
  const [nowSec, setNowSec] = useState(() => {
    const d = new Date();
    return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
  });
  const [dirOpen, setDirOpen] = useState(false);
  const [urgentOn, setUrgentOn] = useState(
    () => { const v = localStorage.getItem("routerev_urgent"); return v === null ? true : v === "true"; }
  );
  const [snooze, setSnooze] = useState({});   // class name -> seconds-since-midnight until which urgent is muted
  const notified = useRef({});
  const urgentFired = useRef({});
  const day = todayName();
  const isWeekday = ["Mon", "Tue", "Wed", "Thu", "Fri"].includes(day);

  const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local
  const onBreak =
    !!semester &&
    ((semester.start_date && todayStr < semester.start_date) ||
      (semester.end_date && todayStr > semester.end_date));
  const active = isWeekday && !onBreak;

  // Always fetch TODAY's plan from live GPS, independent of the day being viewed
  useEffect(() => {
    if (!active) return;
    const loc = userLoc || CENTER;
    api.get(`/plan?day=${day}&lat=${loc[0]}&lng=${loc[1]}&rain=${rain}&accessible=${accessible}&pref=${pref || ""}`)
      .then((r) => setTodayPlan(r.data))
      .catch(() => {});
  }, [userLoc, rain, accessible, classesVersion, day, active, pref]);

  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setNow(d.getHours() * 60 + d.getMinutes());
      setNowSec(d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds());
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const items = useMemo(() => {
    if (!todayPlan) return [];
    return todayPlan.classes.map((c, i) => {
      const travel = todayPlan.legs[i]?.travel;
      const best = travel?.best_min ?? 0;
      const start = toMin(c.start_time);
      return { name: c.name, start, leaveBy: start - best, travelMin: best, mode: travel?.recommended || "walk", live: i === 0 };
    });
  }, [todayPlan]);

  useEffect(() => {
    if (!enabled || !active) return;
    items.forEach((it) => {
      if (now >= it.leaveBy && now < it.start && !notified.current[it.name]) {
        notified.current[it.name] = true;
        const where = it.live ? "from your location" : `by ${it.mode}`;
        const body = `~${it.travelMin} min ${where}. Class at ${label(it.start)}.`;
        try {
          new Notification(`Head out now for ${it.name}`, { body, tag: it.name });
        } catch {}
        toast(`Head out now for ${it.name} — ${it.travelMin} min ${where}`);
      }
    });
  }, [now, enabled, active, items]);

  const toggle = async () => {
    if (!("Notification" in window)) {
      toast.error("Notifications not supported in this browser");
      return;
    }
    if (enabled) {
      setEnabled(false);
      cacheSet("remindersOn", false);
      await disablePush().catch(() => {});
      toast.success("Reminders off");
      return;
    }
    if (Notification.permission !== "granted") {
      const p = await Notification.requestPermission();
      if (p !== "granted") {
        toast.error("Allow notifications to get leave-now reminders");
        return;
      }
    }
    setEnabled(true);
    cacheSet("remindersOn", true);
    const bg = await enablePush({ lat: userLoc?.[0], lng: userLoc?.[1], rain, accessible }).catch(() => false);
    toast.success(bg ? "Reminders on — you'll get a leave-now ping even if the app is closed" : "Leave-now reminders on for today");
  };

  const next = active ? items.find((it) => it.start >= now) : null;
  const nextIdx = next ? items.indexOf(next) : -1;
  const nextLeg = nextIdx >= 0 ? todayPlan?.legs?.[nextIdx] : null;
  const secsToLeave = next ? next.leaveBy * 60 - nowSec : null;
  const NextIcon = next?.mode === "bus" ? Bus : Footprints;

  const directionsOn = (() => {
    const v = localStorage.getItem("routerev_directions");
    return v === null ? true : v === "true";
  })();
  const geom = nextLeg?.travel?.geometry;
  const canDirect = directionsOn && Array.isArray(geom) && geom.length > 1;
  const dirFrom = canDirect ? geom[0] : null;
  const dirTo = canDirect ? geom[geom.length - 1] : null;

  const countdown = (() => {
    if (secsToLeave == null || secsToLeave <= 0) return null;
    const m = Math.floor(secsToLeave / 60);
    const s = secsToLeave % 60;
    if (m >= 60) return `${Math.round(secsToLeave / 60)} min`;
    return `${m}:${String(s).padStart(2, "0")}`;
  })();

  const urgent = urgentOn && !!next && secsToLeave != null && secsToLeave <= 60;
  const snoozedUntil = next ? snooze[next.name] : null;
  const isSnoozed = snoozedUntil != null && nowSec < snoozedUntil;
  const snoozeLeftMin = isSnoozed ? Math.ceil((snoozedUntil - nowSec) / 60) : 0;
  const alerting = urgent && !isSnoozed;

  const doSnooze = async () => {
    if (!next) return;
    setSnooze((s) => ({ ...s, [next.name]: nowSec + 300 }));
    urgentFired.current[next.name] = false;   // allow a fresh nudge after the window
    try { navigator.vibrate?.(0); } catch {}
    try {
      const reg = await navigator.serviceWorker?.ready;
      const sub = await reg?.pushManager?.getSubscription();
      const cid = todayPlan?.classes?.[nextIdx]?.id;
      if (sub?.endpoint && cid) await api.post("/push/snooze-bus", { endpoint: sub.endpoint, class_id: cid });
    } catch {}
    toast("Snoozed 5 min — we'll nudge you again");
  };

  useEffect(() => {
    if (!alerting || !next) return;
    if (urgentFired.current[next.name]) return;
    urgentFired.current[next.name] = true;
    try { navigator.vibrate?.([120, 60, 120]); } catch {}
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
        o.start(); o.stop(ctx.currentTime + 0.42);
        setTimeout(() => ctx.close().catch(() => {}), 700);
      }
    } catch {}
  }, [alerting, next]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mb-5 rounded-xl border border-brand/20 bg-brand text-white overflow-hidden ${alerting ? "urgent-pulse" : ""}`}
      data-testid="reminder-banner"
    >
      <div className="flex items-center justify-between gap-3 p-4 lg:p-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center shrink-0">
            <Clock size={18} strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            {!isWeekday ? (
              <div className="text-sm text-white/85">No classes today — reminders resume on weekdays.</div>
            ) : onBreak ? (
              <div className="text-sm text-white/85">
                On break — reminders paused{semester?.start_date && todayStr < semester.start_date ? ` until ${semester.name || "the semester"} starts` : ""}.
              </div>
            ) : next ? (
              <>
                <div className="mono text-[11px] tracking-[0.2em] uppercase text-white/60">
                  Next up · today {next.live && <span className="text-[#F59E0B]">live</span>}
                  {isSnoozed && <span className="text-white/80"> · snoozed {snoozeLeftMin}m</span>}
                </div>
                <div className="font-semibold truncate flex items-center gap-2" data-testid="leave-countdown">
                  <NextIcon size={15} strokeWidth={1.5} />
                  {countdown ? (
                    <>Leave in <span className={`mono font-bold tabular-nums ${alerting ? "text-[#F59E0B]" : ""}`}>{countdown}</span> for {next.name} · ~{next.travelMin} min {next.live ? "from your spot" : `by ${next.mode}`}</>
                  ) : (
                    <><span className="mono font-bold text-[#F59E0B]">Head out now</span> for {next.name} · ~{next.travelMin} min {next.live ? "from your spot" : `by ${next.mode}`}</>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-white/85">All classes done for today. Enjoy the gap!</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {alerting && (
            <button
              data-testid="snooze-btn"
              onClick={doSnooze}
              className="flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold bg-white/15 hover:bg-white/25 text-white transition-colors"
            >
              <Clock size={14} strokeWidth={2} /> <span className="hidden sm:inline">Snooze 5m</span>
            </button>
          )}
          {next && (
            <button
              data-testid="urgent-toggle"
              onClick={() => setUrgentOn((u) => { localStorage.setItem("routerev_urgent", String(!u)); return !u; })}
              title={urgentOn ? "Urgent leave-now alert on — pulse + buzz in the last 60s" : "Urgent alert off"}
              className={`p-2 rounded-md transition-colors ${urgentOn ? "bg-white/15 hover:bg-white/25 text-white" : "bg-white/10 hover:bg-white/20 text-white/60"}`}
            >
              {urgentOn ? <Zap size={15} strokeWidth={2} /> : <ZapOff size={15} strokeWidth={1.5} />}
            </button>
          )}
          {next && canDirect && (
            <button
              data-testid="start-directions-btn"
              onClick={() => setDirOpen(true)}
              className="flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold bg-white text-brand hover:bg-white/90 transition-colors"
            >
              <Navigation size={14} strokeWidth={2} /> <span className="hidden sm:inline">Directions</span>
            </button>
          )}
          <button
            data-testid="reminder-toggle"
            onClick={toggle}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs font-bold transition-colors ${
              enabled ? "bg-[#F59E0B] text-black" : "bg-white/10 hover:bg-white/20 text-white"
            }`}
          >
            {enabled ? <BellRing size={15} strokeWidth={2} /> : <Bell size={15} strokeWidth={1.5} />}
            <span className="hidden sm:inline">{enabled ? "Reminders on" : "Remind me"}</span>
          </button>
        </div>
      </div>

      <DirectionsModal
        open={dirOpen}
        onClose={() => setDirOpen(false)}
        leg={nextLeg}
        from={dirFrom}
        to={dirTo}
        accessible={accessible}
      />
    </motion.div>
  );
}
